import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Check, Phone, ArrowRight } from "lucide-react";

const companyData = {
  name: "Bright Perfect Heavy Equipment",
  tagline: "Spare Parts Trading L.L.C",
  logo: "https://customer-assets.emergentagent.com/job_115ec36d-1977-41b2-ba39-5f1407060a77/artifacts/28twd3q4_brlogo.png",
  description: "Bright Perfect Heavy Equipment is your trusted partner for premium construction and industrial machinery.",
  longDescription: "We offer sales, rental, and comprehensive maintenance services for a wide range of heavy equipment, backed by expert technical support. With a fleet of over 100 equipment units and a team of skilled technicians, we serve construction companies, infrastructure projects, and industrial facilities across the region. Our commitment to quality and 24/7 support ensures your projects stay on track.",
  services: [
    "Equipment Sales",
    "Short & Long-term Rental",
    "Preventive Maintenance",
    "Emergency Repairs",
    "Operator Training",
    "Equipment Inspection",
    "Fleet Management",
    "Technical Consultation"
  ],
  stats: [
    { value: "100+", label: "Equipment Units" },
    { value: "300+", label: "Clients Served" },
    { value: "24/7", label: "Support Available" },
    { value: "15+", label: "Years Experience" }
  ],
  image: "https://images.pexels.com/photos/1078884/pexels-photo-1078884.jpeg?auto=compress&cs=tinysrgb&w=800",
  heroImage: "https://images.pexels.com/photos/1078884/pexels-photo-1078884.jpeg?auto=compress&cs=tinysrgb&w=1920"
};

export const BrightPerfect = () => {
  return (
    <div className="bg-white">
      {/* Hero Section */}
      <section className="relative min-h-[50vh] flex items-center" data-testid="company-hero">
        <div className="absolute inset-0">
          <img 
            src={companyData.heroImage} 
            alt={companyData.name}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/70 to-black/50" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-20 w-full">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <img 
              src={companyData.logo} 
              alt={companyData.name}
              className="h-24 md:h-32 w-auto mb-6"
            />
            <p className="text-[#F5B800] font-semibold text-lg mb-4">
              {companyData.tagline}
            </p>
            <p className="text-gray-200 text-xl max-w-2xl leading-relaxed">
              {companyData.description}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="bg-[#F5B800] py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {companyData.stats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center"
              >
                <div className="font-heading text-3xl md:text-4xl font-bold text-black">{stat.value}</div>
                <div className="text-black/70 font-medium">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                About {companyData.name}
              </h2>
              <p className="text-gray-600 text-lg leading-relaxed mb-6">
                {companyData.longDescription}
              </p>
              <Link
                to="/companies"
                className="text-[#D4A000] hover:text-[#B89000] font-semibold inline-flex items-center gap-2"
              >
                View All Companies <ArrowRight size={18} />
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <img 
                src={companyData.image} 
                alt={companyData.name}
                className="w-full h-auto shadow-xl"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-16 md:py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Our Services
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Complete heavy equipment solutions from sales to maintenance.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {companyData.services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white p-6 shadow-sm hover:shadow-md transition-shadow"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-[#F5B800] flex items-center justify-center flex-shrink-0">
                    <Check size={20} className="text-black" />
                  </div>
                  <span className="font-semibold text-gray-900">{service}</span>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-16 md:py-20 bg-gray-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-12 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-4">
            Need Heavy Equipment?
          </h2>
          <p className="text-gray-400 mb-8">
            Contact us for equipment sales, rental, and maintenance services.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="https://wa.me/971563419000"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-[#25D366] text-white hover:bg-[#20BA5C] font-bold px-8 py-4 transition-all inline-flex items-center justify-center gap-2"
            >
              <Phone size={20} />
              WhatsApp Us
            </a>
            <Link
              to="/products"
              className="bg-[#F5B800] text-black hover:bg-[#E5AB00] font-bold px-8 py-4 transition-all inline-flex items-center justify-center gap-2"
            >
              Download Catalog
              <ArrowRight size={20} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};
